import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "lucide-react";

const TableOfContents = ({
	content,
	pageName,
	accentColor,
	darkMode = false,
	homeOnly = false,
	inline = false,
}) => {
	const [headings, setHeadings] = useState([]);
	const [activeHeading, setActiveHeading] = useState("");
	const tocRef = useRef(null);
	const navigate = useNavigate();

	useEffect(() => {
		// Parse markdown content to extract headings
		const parseHeadings = (markdownContent) => {
			if (!markdownContent) return [];

			const lines = markdownContent.split("\n");
			const extractedHeadings = [];

			lines.forEach((line) => {
				const trimmedLine = line.trim();
				if (trimmedLine.startsWith("#")) {
					const level = trimmedLine.match(/^#+/)[0].length;
					if (level <= 3) {
						// Only h1, h2, h3
						const text = trimmedLine.replace(/^#+\s*/, "");
						const id = text
							.toLowerCase()
							.replace(/[^a-z0-9]+/g, "-")
							.replace(/(^-|-$)/g, "");
						extractedHeadings.push({
							level,
							text,
							id,
						});
					}
				}
			});

			return extractedHeadings;
		};

		// Combine all content sections and parse headings
		const allContent = Array.isArray(content)
			? content.join("\n")
			: content || "";
		const parsedHeadings = parseHeadings(allContent);
		setHeadings(parsedHeadings);
	}, [content]);

	// Calculate TOC width and communicate to page content
	useEffect(() => {
		if (tocRef.current) {
			const tocWidth = 200; // Fixed width since always expanded
			const tocLeft = 16;
			const tocRightEdge = tocLeft + tocWidth;

			document.documentElement.style.setProperty(
				"--toc-width",
				`${tocWidth}px`
			);
			document.documentElement.style.setProperty(
				"--toc-right-edge",
				`${tocRightEdge}px`
			);
		}
	}, [headings]);

	useEffect(() => {
		let ticking = false;

		const handleScroll = () => {
			if (!ticking) {
				requestAnimationFrame(() => {
					if (headings.length > 0) {
						const headingElements = headings
							.map((h) => document.getElementById(h.id))
							.filter(Boolean);

						let activeId = "";
						const viewportThreshold = 100;

						// Find the currently active heading
						for (let i = headingElements.length - 1; i >= 0; i--) {
							const element = headingElements[i];
							if (
								element &&
								element.getBoundingClientRect().top <= viewportThreshold
							) {
								activeId = element.id;
								break;
							}
						}

						setActiveHeading(activeId);
					}
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll(); // Initial call

		return () => window.removeEventListener("scroll", handleScroll);
	}, [headings]);

	const scrollToHeading = (id) => {
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	const getLineStyle = (heading, index) => {
		const isActive = activeHeading === heading.id;
		const baseWidth = heading.level === 1 ? 24 : heading.level === 2 ? 18 : 12;

		return {
			width: `${baseWidth}px`,
			height: isActive ? "2px" : "1px",
			backgroundColor: isActive
				? accentColor
				: "rgba(107, 114, 128, 0.8)",
		};
	};

	if (homeOnly) {
		return (
			<div className="fixed left-4 top-4 z-40 hidden lg:block">
				<div className="p-2">
					<button
						className="flex items-center font-semibold space-x-2 text-gray-700 hover:text-gray-900 transition-all duration-200 text-sm px-2 py-1.5"
						onClick={() => navigate("/")}
					>
						<ArrowLeftIcon className="w-4 h-4 transition-transform duration-200 hover:translate-x-[-2px]" />
						<span>Back</span>
					</button>
				</div>
			</div>
		);
	}

	// Calculate dynamic height based on content
	const getContainerHeight = () => {
		const baseHeight = 40; // Back button + padding (reduced)
		const headingsHeight = headings.length * 28; // Each heading line (reduced)
		const extraSpacing = 10; // Additional spacing (reduced)
		return baseHeight + headingsHeight + extraSpacing;
	};

	const outerClassName = inline
		? "relative z-40 hidden lg:block"
		: "fixed left-4 top-1/2 transform -translate-y-1/2 z-40 hidden lg:block";

	return (
		<div className={outerClassName}>
			<div
				className="relative w-48 p-3"
				ref={tocRef}
				style={{
					height: `${getContainerHeight()}px`,
				}}
			>
				{/* Back button */}
				<div className="mb-3">
					<button
						className="flex items-center font-semibold space-x-2 text-white text-sm pl-2 pr-3 py-1 rounded-full group"
						style={{
							backgroundColor: accentColor,
							boxShadow: `0 -2px 1px 0 rgba(0, 0, 0, 0.08) inset, 0 2px 1px 0 rgba(255, 255, 255, 0.5) inset, 0 2px 5px -1px rgba(0, 0, 0, 0.05), 0 1px 3px -1px rgba(0, 0, 0, 0.3)`,
						}}
						onClick={() => navigate("/")}
					>
						<ArrowLeftIcon className="w-3 h-3 group-hover:translate-x-[-2px] transition-transform duration-200" />
						<span>Back</span>
					</button>
				</div>

				{/* Page name */}
				{/* <div className="mb-4 pb-3">
					<h3 className="text-sm font-semibold text-gray-800">{pageName}</h3>
				</div> */}

				{/* Headings */}
				{headings.length > 0 && (
					<div className="relative">
						{/* Headings list */}
						<div className="flex flex-col">
							{headings.map((heading, index) => (
								<div
									key={index}
									className="flex items-center select-none cursor-pointer"
									style={{ height: "28px" }}
									onClick={() => scrollToHeading(heading.id)}
								>
									<div className="flex items-center px-2 w-full h-full">
										{/* Line visualization */}
										<div
											className="rounded-full"
											style={getLineStyle(heading, index)}
										/>

										{/* Heading text */}
										<span
											className={`ml-2 text-xs hover:font-medium  overflow-ellipsis line-clamp-1 ${
												activeHeading === heading.id
													? "font-medium text-gray-900"
													: "text-gray-600"
											} ${
												heading.level === 1
													? ""
													: heading.level === 2
													? "ml-1"
													: "ml-2"
											}`}
										>
											{heading.text}
										</span>
									</div>
								</div>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default TableOfContents;
